import { Banner, BlockStack, Box, Button, Card, InlineStack, Layout, Page, Pagination, Text } from '@shopify/polaris';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agent, { FitmentImportHistoryItem } from '../../app/api/agent';
import ImportHistoryCards from './ImportHistoryCards';

const pageSize = 20;

const ImportHistoryPage = () => {
    const navigate = useNavigate();
    const [history, setHistory] = useState<FitmentImportHistoryItem[]>([]);
    const [historyError, setHistoryError] = useState<string | undefined>();
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [archivingIds, setArchivingIds] = useState<Record<number, boolean>>({});
    const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});
    const [page, setPage] = useState(1);

    const loadHistory = useCallback(async () => {
        try {
            setIsLoadingHistory(true);
            setHistoryError(undefined);
            const result = await agent.Imports.history();
            setHistory(result);
        } catch (error: any) {
            const message = error?.response?.data || error?.message || 'Failed to load import history.';
            setHistoryError(String(message));
        } finally {
            setIsLoadingHistory(false);
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const totalPages = Math.max(1, Math.ceil(history.length / pageSize));

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const currentItems = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return history.slice(startIndex, startIndex + pageSize);
    }, [history, page]);

    const handleArchive = useCallback(async (queueId: number) => {
        try {
            setArchivingIds((prev) => ({ ...prev, [queueId]: true }));
            await agent.Imports.archive(queueId);
            setHistory((prev) => prev.filter((item) => item.queueId !== queueId));
        } catch (error: any) {
            const message = error?.response?.data || error?.message || 'Failed to archive import history item.';
            setHistoryError(String(message));
        } finally {
            setArchivingIds((prev) => ({ ...prev, [queueId]: false }));
        }
    }, []);

    const handleDownload = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.download(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = item.originalFileName || `import-${item.queueId}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            const message = error?.response?.data || error?.message || 'Failed to download import file.';
            setHistoryError(String(message));
        } finally {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    return (
        <Page
            title="Import history"
            backAction={{ content: 'Imports', onAction: () => navigate('/imports') }}
        >
            <Layout>
                <Layout.Section>
                    <Card>
                        <BlockStack gap="300">
                            <InlineStack align="space-between">
                                <Text as="h2" variant="headingMd">All imports</Text>
                                <Button onClick={loadHistory} loading={isLoadingHistory}>Refresh</Button>
                            </InlineStack>

                            {historyError && (
                                <Banner tone="critical" title="Unable to load import history">
                                    <p>{historyError}</p>
                                </Banner>
                            )}

                            {history.length === 0 && !isLoadingHistory ? (
                                <Text as="p" tone="subdued">No import history yet.</Text>
                            ) : (
                                <>
                                    <ImportHistoryCards
                                        items={currentItems}
                                        archivingIds={archivingIds}
                                        downloadingIds={downloadingIds}
                                        onArchive={handleArchive}
                                        onDownload={handleDownload}
                                    />
                                    <Box paddingBlockStart="200">
                                        <InlineStack align="center">
                                            <Pagination
                                                hasPrevious={page > 1}
                                                onPrevious={() => setPage((prev) => Math.max(1, prev - 1))}
                                                hasNext={page < totalPages}
                                                onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                            />
                                        </InlineStack>
                                    </Box>
                                </>
                            )}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default ImportHistoryPage;
